import Sidebar from "@/layouts/Sidebar";
import { adminRouteRegistry, findAdminRoute } from "@/routes/admin-route-registry";
import { tenantService } from "@/services/tenant.service";
import { useAuthStore } from "@/stores/authStore";
import { Bell, Building2, LogOut, Menu, Search, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const tenantId = useMemo(() => {
    const match = location.pathname.match(/^\/tenants\/([0-9a-f-]{30,})(?:\/|$)/i);
    return match?.[1] ?? "";
  }, [location.pathname]);
  const tenantContext = useQuery({
    queryKey: ["admin-tenant", tenantId],
    queryFn: () => tenantService.getById(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
  const tenantSearch = useQuery({
    queryKey: ["admin-global-tenant-search", searchTerm.trim()],
    queryFn: () => tenantService.list({ page: 1, limit: 6, search: searchTerm.trim() }),
    enabled: searchTerm.trim().length >= 2,
    staleTime: 15_000,
  });
  const routeMatches = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (needle.length < 2) return [];
    return adminRouteRegistry
      .filter((route) => !route.path.includes(":"))
      .filter((route) => `${route.title} ${route.subtitle}`.toLowerCase().includes(needle))
      .slice(0, 5);
  }, [searchTerm]);

  useEffect(() => {
    setSearchOpen(false);
    setSearchTerm("");
  }, [location.pathname]);

  const { title, subtitle } = useMemo(() => {
    const route = findAdminRoute(location.pathname);
    if (route) return { title: route.title, subtitle: route.subtitle };
    if (location.pathname === "/forbidden") {
      return { title: "Access denied", subtitle: "Restricted control" };
    }
    return { title: "Admin", subtitle: "SaaS control plane" };
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen bg-slate-50 relative overflow-hidden">
      {/* Decorative background element */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 -z-10" />

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 flex h-20 items-center justify-between border-b border-slate-200 bg-white/80 backdrop-blur-md px-6 lg:px-10">
          <div className="flex min-w-0 items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-xl lg:hidden transition-all"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="hidden sm:block">
              <h2 className="text-xl font-black text-slate-900 tracking-tight">
                {title}
              </h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                {subtitle}
              </p>
            </div>
            {tenantContext.data && (
              <button onClick={() => navigate(`/tenants/${tenantId}/control-plane`)} className="hidden max-w-56 items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-left lg:flex">
                <Building2 className="h-4 w-4 shrink-0 text-blue-600" />
                <span className="min-w-0"><span className="block truncate text-xs font-bold text-blue-900">{tenantContext.data.name}</span><span className="block text-[9px] font-bold uppercase text-blue-500">Tenant context</span></span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="relative hidden md:block">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                value={searchTerm}
                onFocus={() => setSearchOpen(true)}
                onChange={(event) => { setSearchTerm(event.target.value); setSearchOpen(true); }}
                onKeyDown={(event) => { if (event.key === "Escape") setSearchOpen(false); }}
                placeholder="Search tenants or controls"
                className="h-10 w-64 rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none transition focus:w-80 focus:border-blue-300 focus:bg-white"
              />
              {searchOpen && searchTerm.trim().length >= 2 && (
                <div className="absolute right-0 top-12 z-50 w-[420px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
                  <div className="border-b border-slate-100 px-4 py-2 text-[10px] font-bold uppercase text-slate-400">Tenants</div>
                  {(tenantSearch.data?.data ?? []).map((tenant) => <button key={tenant.id} onClick={() => navigate(`/tenants/${tenant.id}/control-plane`)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"><Building2 className="h-4 w-4 text-blue-600" /><span><span className="block text-sm font-bold text-slate-800">{tenant.name}</span><span className="block text-xs text-slate-400">{tenant.email} · {tenant.status}</span></span></button>)}
                  <div className="border-y border-slate-100 px-4 py-2 text-[10px] font-bold uppercase text-slate-400">Controls</div>
                  {routeMatches.map((route) => <button key={route.path} onClick={() => navigate(route.path)} className="block w-full px-4 py-3 text-left hover:bg-slate-50"><span className="block text-sm font-bold text-slate-800">{route.title}</span><span className="block text-xs text-slate-400">{route.subtitle}</span></button>)}
                  {!tenantSearch.isLoading && !(tenantSearch.data?.data?.length || routeMatches.length) && <p className="px-4 py-5 text-sm text-slate-500">No tenant or control found.</p>}
                </div>
              )}
            </div>
            <button className="p-2.5 text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-all relative">
              <Bell className="h-5 w-5" />
              <span className="absolute top-2.5 right-2.5 h-2 w-2 rounded-full bg-blue-500 border-2 border-white" />
            </button>

            <div className="h-8 w-px bg-slate-200 mx-2" />

            <div className="flex items-center gap-3 pl-2">
              <div className="hidden md:flex flex-col items-end text-right">
                <p className="text-sm font-black text-slate-900 leading-none">
                  {user?.name || "Administrator"}
                </p>
                <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mt-1">
                  {user?.role || "Super Admin"}
                </p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center border border-slate-200 group relative">
                <User className="h-5 w-5 text-slate-600" />

                {/* Simple dropdown indicator or effect could go here */}
              </div>

              <button
                onClick={handleLogout}
                className="ml-2 p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all group"
                title="Sign out"
              >
                <LogOut className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-10">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminLayout;
